



import React, { useEffect, useState } from "react";
import { getAdminStatistics } from "../Component/Servicefile/dashboardService.jsx";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Avatar,
  Stack,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  LinearProgress,
} from "@mui/material";
import {
  Store,
  Restaurant,
  People,
  ShoppingCart,
  CurrencyRupee,
  SupportAgent,
  Campaign,
  LocalShipping,
  EmojiEvents,
  WorkspacePremium,
  MilitaryTech,
  Star,
} from "@mui/icons-material";

import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";

// ---------- THEME (orange replaces blue) ----------
const PRIMARY = "#ff6b00";
const PRIMARY_DARK = "#c43e00";
const PRIMARY_LIGHT = "#ffb74d";

const kpis = [
  {
    title: "Total Restaurants",
    value: "1,980",
    icon: <Restaurant />,
  },
  {
    title: "Total Users",
    value: "18,500",
    icon: <People />,
  },
  {
    title: "Total Riders",
    value: "890",
    icon: <LocalShipping />,
  },
  {
    title: "Total Companies",
    value: "325",
    icon: <Store />,
  },
  
  {
    title: "Total Deliveries",
    value: "1,25,450",
    icon: <ShoppingCart />,
  },
  {
    title: "Total Revenue",
    value: "₹2.8 Cr",
    icon: <CurrencyRupee />,
  },
  {
    title: "Active Campaigns",
    value: "67",
    icon: <Campaign />,
  },
  {
    title: "Open Tickets",
    value: "124",
    icon: <SupportAgent />,
  },
];

const pieData = [
  { name: "Restaurants", value: 45 },
  { name: "Hotels", value: 20 },
  { name: "Cafes", value: 15 },
  { name: "Food Trucks", value: 10 },
  { name: "Others", value: 10 },
];

const PIE_COLORS = ["#ff6b00", "#ff9800", "#ffb74d", "#ffcc80", "#ffe0b2"];

const revenueData = [
  { month: "Jan", revenue: 12 },
  { month: "Feb", revenue: 18 },
  { month: "Mar", revenue: 22 },
  { month: "Apr", revenue: 20 },
  { month: "May", revenue: 30 },
  { month: "Jun", revenue: 42 },
];




// Sorted best -> worst performer (by progress score)
const riders = [
  { name: "Ramesh Kumar", sales: "320 Orders", rating: "4.9", progress: 97 },
  { name: "Suresh Yadav", sales: "295 Orders", rating: "4.8", progress: 91 },
  { name: "Amit Singh", sales: "270 Orders", rating: "4.7", progress: 84 },
  { name: "Vikram Rao", sales: "240 Orders", rating: "4.5", progress: 72 },
].sort((a, b) => b.progress - a.progress);

// Rank badge - gold / silver / bronze for top 3, star for rest
const RankBadge = ({ rank }) => {
  if (rank === 1) {
    return (
      <Avatar sx={{ bgcolor: "#FFD700", width: 34, height: 34 }}>
        <EmojiEvents sx={{ fontSize: 20, color: "#7a5b00" }} />
      </Avatar>
    );
  }
  if (rank === 2) {
    return (
      <Avatar sx={{ bgcolor: "#C0C0C0", width: 34, height: 34 }}>
        <WorkspacePremium sx={{ fontSize: 20, color: "#4a4a4a" }} />
      </Avatar>
    );
  }
  if (rank === 3) {
    return (
      <Avatar sx={{ bgcolor: "#CD7F32", width: 34, height: 34 }}>
        <MilitaryTech sx={{ fontSize: 20, color: "#fff" }} />
      </Avatar>
    );
  }
  return (
    <Avatar sx={{ bgcolor: "#fdebd9", width: 34, height: 34 }}>
      <Star sx={{ fontSize: 18, color: PRIMARY }} />
    </Avatar>
  );
};

// Reusable ranked performance table (used for both restaurants & riders)
const PerformanceTable = ({ title, badge, nameLabel, salesLabel, data }) => (
  <Paper sx={{ p: 3, borderRadius: 4, height: "auto", mt: 3 }}>
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      mb={2}
    >
      <Typography variant="h6" fontWeight={700}>
        {title}
      </Typography>
      <Chip
        label={badge}
        size="small"
        sx={{
          bgcolor: "#fff1e0",
          color: PRIMARY_DARK,
          fontWeight: 600,
        }}
      />
    </Stack>

    <Box sx={{ overflowX: "auto" }}>
      <Table sx={{ minWidth: 480 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700, color: "text.secondary" }}>
              Rank
            </TableCell>
            <TableCell sx={{ fontWeight: 700, color: "text.secondary" }}>
              {nameLabel}
            </TableCell>
            <TableCell sx={{ fontWeight: 700, color: "text.secondary" }}>
              {salesLabel}
            </TableCell>
            <TableCell sx={{ fontWeight: 700, color: "text.secondary" }}>
              Rating
            </TableCell>
            <TableCell
              sx={{ fontWeight: 700, color: "text.secondary" }}
              width={180}
            >
              Performance
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {data.map((item, index) => {
            const rank = index + 1;
            return (
              <TableRow
                key={item.name}
                sx={{
                  "&:last-child td": { border: 0 },
                  ...(rank === 1 && { bgcolor: "#fff8ee" }),
                }}
              >
                <TableCell>
                  <RankBadge rank={rank} />
                </TableCell>

                <TableCell>
                  <Typography fontWeight={600}>{item.name}</Typography>
                </TableCell>

                <TableCell>
                  <Typography sx={{ color: PRIMARY, fontWeight: 700 }}>
                    {item.sales}
                  </Typography>
                </TableCell>

                <TableCell>
                  <Stack direction="row" alignItems="center" gap={0.5}>
                    <Star sx={{ fontSize: 16, color: "#ffb300" }} />
                    <Typography variant="body2">{item.rating}</Typography>
                  </Stack>
                </TableCell>

                <TableCell>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <LinearProgress
                      variant="determinate"
                      value={item.progress}
                      sx={{
                        flex: 1,
                        height: 8,
                        borderRadius: 5,
                        bgcolor: "#fde6cf",
                        "& .MuiLinearProgress-bar": {
                          backgroundColor: PRIMARY,
                          borderRadius: 5,
                        },
                      }}
                    />
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{ width: 36 }}
                    >
                      {item.progress}%
                    </Typography>
                  </Stack>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  </Paper>
);

const KPI = ({ item }) => (
  <Paper
    elevation={0}
    sx={{
      p: 3,
      borderRadius: 4,
      height: "100%",
      // background: "linear-gradient(135deg,#ffffff,#fff8f0)",
      border: "1px solid #f0e0d0",
      transition: ".3s",
      "&:hover": {
        transform: "translateY(-6px)",
        boxShadow: "0 15px 30px rgba(255,107,0,.18)",
      },
    }}
  >
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Box>
        <Typography variant="body2" color="text.secondary">
          {item.title}
        </Typography>
        <Typography variant="h5" fontWeight={700} mt={1}>
          {item.value}
        </Typography>
      </Box>

      <Avatar sx={{ bgcolor: PRIMARY, width: 56, height: 56 }}>
        {item.icon}
      </Avatar>
    </Stack>
  </Paper>
);


export default function SuperAdminDashboard() {
  const [restaurants, setRestaurants] = useState([]);
  const [salesAnalytics, setSalesAnalytics] = useState([]);
useEffect(() => {
    fetchStatistics();
  }, []);

  const fetchStatistics = async () => {
    try {
      const data = await getAdminStatistics(
        "2026-07-01",
        "2026-07-08"
      );

      const restaurantData =
  data?.topPerformingVendors?.map((vendor) => ({
    id: vendor.vendorId,
    name: `Restaurant ${vendor.vendorId}`,
    sales: `₹${vendor.revenue.toFixed(2)}`,
    rating: data.averageRating,
    progress: parseFloat(vendor.percentage),
  })) || [];

setRestaurants(restaurantData);

      const chartData =
        data?.topPerformingVendors?.map((vendor) => ({
          name: `Vendor ${vendor.vendorId}`,
          revenue: vendor.revenue,
        })) || [];

      setSalesAnalytics(chartData);
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <Box
      sx={{
        // height: "100vh",
        overflow: "hidden",
       
        p: 3,
        pt: 5,
        boxSizing: "border-box",
        marginTop:'37px',
      }}
    >
      {/* MAIN CONTENT GRID */}
      {/* <Paper
        sx={{
          mb: 4,
          p: 4,
          borderRadius: 5,
          background: `linear-gradient(135deg, ${PRIMARY}, ${PRIMARY_DARK})`,
          color: "#fff",
          width: "100%",
        }}
      >
        <Typography variant="h4" fontWeight={800}>
          Welcome To Super Admin Dashboard 👋
        </Typography>
        <Typography mt={1}>
          Monitor businesses, vendors, restaurants, users, revenue and
          platform activities in real time.
        </Typography>
      </Paper> */}
      <Grid container spacing={3} sx={{ height: "calc(100vh - 70px)",scrollbarWidth:'none' }}>
        {/* LEFT COLUMN - Scrollable independently */}
        <Grid item xs={12} lg={9} sx={{ height: "100%" }}>
          <Box
            sx={{
              height: "100%",
              overflowY: "auto",
              pr: 1,
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-track": {
                background: "#f1f1f1",
                borderRadius: "10px",
              },
              "&::-webkit-scrollbar-thumb": {
                background: PRIMARY,
                borderRadius: "10px",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: PRIMARY_DARK,
              },
              scrollbarWidth:'none'
            }}
          >
            {/* KPI CARDS */}
            <Grid container spacing={3}>
              {kpis.map((item) => (
                <Grid item xs={12} sm={6} md={3} key={item.title}>
                  <KPI item={item} />
                </Grid>
              ))}
            </Grid>

            {/* RESTAURANT TABLE */}
            <PerformanceTable
  title="Top Performing Restaurants"
  badge="Ranked by performance"
  nameLabel="Restaurant"
  salesLabel="Sales"
  data={restaurants}
/>

            {/* RIDER TABLE */}
            <PerformanceTable
              title="Top Performing Riders"
              badge="Ranked by performance"
              nameLabel="Rider"
              salesLabel="Deliveries"
              data={riders}
            />

            {/* Spacer for better scrolling */}
            <Box sx={{ height: 2 }} />
          </Box>
        </Grid>

        {/* RIGHT COLUMN - Fixed / Sticky Sidebar - Scrolls independently */}
        <Grid item xs={12} lg={3} sx={{ height: "100%",scrollbarWidth:'none' }}>
          <Box
            sx={{
              height: "100%",
              overflowY: "auto",
              scrollbarWidth:'none',
              border: "1px solid rgba(255,255,255,0.3)",
              
              pr: 0.5,
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-track": {
                // background: "#f1f1f1",
                borderRadius: "10px",
              },
              "&::-webkit-scrollbar-thumb": {
                background: PRIMARY,
                borderRadius: "10px",
                
              },
            }}
          >
            <Stack spacing={3}>
              {/* Revenue Chart */}
              <Paper sx={{ p: 3, borderRadius: 4, backgroundColor: "#e66d3321" }}>
  <Typography variant="h6" fontWeight={700} mb={2}>
    Sales Analytics
  </Typography>

  <ResponsiveContainer width="100%" height={250}>
    <LineChart data={salesAnalytics}>
      <CartesianGrid strokeDasharray="3 3" />
      <XAxis dataKey="name" />
      <YAxis />
      <Tooltip
        formatter={(value) => [`₹${value}`, "Revenue"]}
      />
      <Line
        type="monotone"
        dataKey="revenue"
        stroke={PRIMARY}
        strokeWidth={3}
      />
    </LineChart>
  </ResponsiveContainer>
</Paper>

              {/* Pie Chart */}
              <Paper sx={{ p: 3, borderRadius: 4,backgroundColor:"#e66d3321" }}>
                <Typography variant="h6" fontWeight={700} mb={2}>
                  Business Distribution
                </Typography>

                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie data={pieData} dataKey="value" outerRadius={90} label>
                      {pieData.map((entry, index) => (
                        <Cell key={index} fill={PIE_COLORS[index]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </Paper>

              {/* Recent Activity */}
              <Paper sx={{ p: 3, borderRadius: 4,backgroundColor:"#e66d3321" }}>
                <Typography variant="h6" fontWeight={700} mb={2}>
                  Recent Activities
                </Typography>

                <Stack spacing={2}>
                  <Typography>🍔 New Restaurant Approved</Typography>
                  <Typography>👤 250 New Users Joined</Typography>
                  <Typography>💰 ₹12L Revenue Generated</Typography>
                  <Typography>🎯 New Campaign Started</Typography>
                  <Typography>🎟️ 18 Support Tickets Closed</Typography>
                </Stack>
              </Paper>
            </Stack>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}